import { useState, useRef, useEffect, useCallback } from "react";
import { CheckCircle2, MapPin, AlertCircle, AlertTriangle, RotateCcw, ScanLine, Lock, Truck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useActiveStore } from "@/hooks/use-active-store";
import { useLookupOrder, useFulfillOrder, usePickPackSettings } from "@/hooks/use-fulfillment";
import { PickPackSettingsDialog } from "@/components/fulfillment/pick-pack-settings-dialog";
import { CourierShipmentDialog } from "@/components/wholesale/courier-shipment-dialog";
import { cn } from "@/lib/utils";
import { playSuccess, playError, playComplete } from "@/lib/sounds";
import {
  getScannedCount,
  incrementScannedItem,
  setScannedItemOverride,
  type ScannedItem,
} from "@/lib/fulfillment/scan-state";
import { isolateScannerKeys } from "@/hooks/use-scanner-input";
import { MetaLookupHeader, MetaSourcesLine, MetaOrderTag, memberLabel } from "@/components/meta-orders/meta-badge";
import type { MetaLookup } from "@/services/fulfillment";

const META_PACK_TOAST = "meta-pack-failed";

type MetaMemberOutcome = { key: string; label: string; ok: boolean; error?: string };

/** Poruka servera kad je namjerna i na bosanskom (npr. „Narudžba VP-… je otkazana“, „Nemate
 *  pristup …“, „Meta narudžba M-12 nema nalog …“); tehničke greške WooCommercea (engleski, kodovi)
 *  ostaju iza opšte poruke. */
function lookupErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  return /^(Narudžba|Spojena pošiljka|Poštarina nije ažurirana|Nemate pristup)/.test(message) ? message : "Narudžba nije pronađena";
}

/** Rezultat packa po članu, s imenom shopa iz lookup-a ("Cherokee #10452"). Redoslijed članova je
 *  isti u lookup-u i u odgovoru packa; broj narudžbe je rezerva ako se liste razlikuju. */
function metaMemberOutcome(meta: MetaLookup, results: { number: string; ok: boolean; error?: string }[]): MetaMemberOutcome[] {
  return results.map((r, i) => {
    const m = meta.members[i]?.number === r.number ? meta.members[i] : meta.members.find((x) => x.number === r.number);
    return {
      key: m ? `${m.storeId}:${m.orderId}` : `${i}:${r.number}`,
      label: memberLabel(m?.storeName, r.number),
      ok: r.ok,
      error: r.error,
    };
  });
}

export function PackPage() {
  const { storeId } = useActiveStore();
  const fulfillOrder = useFulfillOrder();
  const { data: displaySettings } = usePickPackSettings(storeId);
  const imgSize = displaySettings?.imageSize ?? 40;
  const locFontSize = displaySettings?.locationFontSize ?? 12;
  const hideWarehouseName = displaySettings?.hideWarehouseName ?? false;

  const [autoFulfillment, setAutoFulfillment] = useState(() => {
    const stored = localStorage.getItem("komoran:autoFulfillment");
    return stored !== null ? stored === "true" : true;
  });

  const [orderInput, setOrderInput] = useState("");
  const [activeOrderQuery, setActiveOrderQuery] = useState("");
  const [skuInput, setSkuInput] = useState("");
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [showAlternates, setShowAlternates] = useState<string | null>(null);
  // `needsShipment`: veleprodajna narudžba spakovana bez naloga u brzoj pošti — ekran ne ide
  // sam na sljedeću narudžbu nego nudi nalog i naljepnicu.
  const [completionState, setCompletionState] = useState<{
    orderId: string;
    orderNumber: string;
    codAmount?: number;
    needsShipment?: boolean;
    meta?: { number: string; storeNames: string[]; members: MetaMemberOutcome[] };
  } | null>(null);
  const [shipmentOpen, setShipmentOpen] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  // Meta pack koji nije završio sve članove: ko je spakovan, ko nije i zašto. Ponovni pack
  // dovršava samo preostale (server preskače spakovane).
  const [metaOutcome, setMetaOutcome] = useState<MetaMemberOutcome[] | null>(null);

  const orderInputRef = useRef<HTMLInputElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const packInFlightRef = useRef(false);
  const shipmentCreatedRef = useRef(false);

  const { data: lookupData, isLoading, error: lookupError, refetch: refetchLookup } = useLookupOrder(storeId, activeOrderQuery, "pack");

  const order = lookupData?.order;
  const pickItems = lookupData?.pickItems;
  const orderId = lookupData?.orderId;
  // The resolved store may differ from sidebar storeId when the user scanned a
  // tracking number whose shipment belongs to another shop. Use the resolved
  // value for the completion call so we don't complete the wrong order.
  const resolvedStoreId: string | undefined = lookupData?.storeId;
  // Meta narudžba (M-12): zbirna lista svih članova; POST ide s orderId "M-12" kao i obična.
  const meta: MetaLookup | null = lookupData?.meta ?? null;

  const totalNeeded = pickItems?.reduce((sum: number, item: any) => sum + item.quantity, 0) ?? 0;
  const totalScanned = scannedItems.reduce((sum, s) => sum + s.scannedCount, 0);
  const allDone = totalNeeded > 0 && totalScanned >= totalNeeded;
  const progressPct = totalNeeded > 0 ? Math.round((totalScanned / totalNeeded) * 100) : 0;

  useEffect(() => {
    orderInputRef.current?.focus();
  }, []);

  useEffect(() => {
    // Na ekranu završetka narudžba je već obrađena — kasniji refetch (npr. nakon naloga u
    // brzoj pošti) ne smije javiti grešku za nju.
    if (lookupError && activeOrderQuery && !completionState) {
      playError();
      toast.error(lookupErrorMessage(lookupError));
      setActiveOrderQuery("");
      setOrderInput("");
      setTimeout(() => orderInputRef.current?.focus(), 100);
    }
  }, [lookupError]);

  useEffect(() => {
    if (pickItems && skuInputRef.current) {
      skuInputRef.current.focus();
    }
  }, [pickItems]);

  const resetState = useCallback(() => {
    setActiveOrderQuery("");
    setOrderInput("");
    setSkuInput("");
    setScannedItems([]);
    setShowAlternates(null);
    setCompletionState(null);
    setCompletionError(null);
    setMetaOutcome(null);
    setShipmentOpen(false);
    shipmentCreatedRef.current = false;
    packInFlightRef.current = false;
    setTimeout(() => orderInputRef.current?.focus(), 100);
  }, []);

  const doFulfill = useCallback(() => {
    if (!orderId || !resolvedStoreId || completionState || packInFlightRef.current) return;
    packInFlightRef.current = true;
    setCompletionError(null);
    fulfillOrder.mutate(
      { orderId, storeId: resolvedStoreId, itemsJson: JSON.stringify(scannedItems) },
      {
        onSuccess: (result) => {
          const outcome = meta && result.members ? metaMemberOutcome(meta, result.members) : null;
          if (outcome?.some((m) => !m.ok)) {
            const failed = outcome.filter((m) => !m.ok).map((m) => m.label).join(", ");
            setMetaOutcome(outcome);
            setCompletionError(`Nisu završene: ${failed}`);
            playError();
            toast.error(`Pack nije završen za: ${failed}`, { id: META_PACK_TOAST });
            // Osvježi "spakovano" po članu u zaglavlju.
            refetchLookup();
            return;
          }
          toast.dismiss(META_PACK_TOAST);
          playComplete();
          // Server odlučuje u lookup-u: potvrđena/spakovana veleprodajna narudžba bez ikakvog
          // trackinga (ni pošiljke ni ručno upisanog na narudžbu).
          const needsShipment = !!lookupData?.wholesale?.needsShipment;
          setCompletionState({
            orderId,
            orderNumber: order?.number || orderId,
            codAmount: result.codAmount,
            needsShipment,
            ...(meta && outcome ? { meta: { number: meta.number, storeNames: meta.storeNames, members: outcome } } : {}),
          });
          if (!needsShipment) setTimeout(resetState, 3000);
        },
        onError: (err) => {
          const message = err instanceof Error ? err.message : "Greška pri završavanju narudžbe";
          setCompletionError(message);
          playError();
          toast.error(`Greška: ${message}`);
        },
        onSettled: () => {
          packInFlightRef.current = false;
        },
      },
    );
  }, [completionState, fulfillOrder, lookupData?.wholesale?.needsShipment, meta, order?.number, orderId, refetchLookup, resetState, resolvedStoreId, scannedItems]);

  useEffect(() => {
    if (allDone && autoFulfillment && !completionError) {
      doFulfill();
    }
  }, [allDone, autoFulfillment, completionError, doFulfill]);

  function handleOrderSubmit(e: React.KeyboardEvent) {
    if (e.key === "Enter" && orderInput.trim()) {
      e.preventDefault();
      setActiveOrderQuery(orderInput.trim());
      setScannedItems([]);
      setCompletionState(null);
      setCompletionError(null);
      setMetaOutcome(null);
      packInFlightRef.current = false;
    }
  }

  function handleSkuScan(e: React.KeyboardEvent) {
    if (e.key !== "Enter" || !skuInput.trim() || !pickItems) return;
    e.preventDefault();

    const sku = skuInput.trim();
    setSkuInput("");

    const item = pickItems.find((p: any) => p.sku === sku);
    if (!item) {
      playError();
      toast.error("SKU nije u narudžbi");
      return;
    }

    const currentCount = getScannedCount(scannedItems, sku);

    if (currentCount >= item.quantity) {
      playError();
      toast.error(`Već skenirano ${item.quantity}x ${sku}`);
      return;
    }

    setScannedItems((prev) => incrementScannedItem(prev, sku));

    playSuccess();
    toast.success(`${sku} skeniran`);
  }

  function handleItemTap(sku: string, quantity: number) {
    const currentCount = getScannedCount(scannedItems, sku);

    if (currentCount >= quantity) return;

    setScannedItems((prev) => incrementScannedItem(prev, sku));

    playSuccess();
  }

  function handleOverride(sku: string, binLabel: string) {
    setScannedItems((prev) => setScannedItemOverride(prev, sku, binLabel));
    setShowAlternates(null);
    toast.info(`Lokacija promijenjena na ${binLabel}`);
  }

  if (completionState) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-4">
        <div className="ring-1 ring-green-600/30 bg-green-600/5 p-8 flex flex-col items-center gap-3">
          <CheckCircle2 className="h-10 w-10 text-green-600" />
          {completionState.meta ? (
            <>
              <p className="text-sm font-medium">Spojena pošiljka {completionState.meta.number} završena</p>
              <MetaOrderTag number={completionState.meta.number} storeNames={completionState.meta.storeNames} />
              <ul className="text-xs text-muted-foreground tabular-nums text-center">
                {completionState.meta.members.map((m) => (
                  <li key={m.key}>{m.label} · završena</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm font-medium">Narudžba #{completionState.orderNumber} završena</p>
          )}
          {completionState.codAmount != null && completionState.codAmount > 0 && (
            <div className="ring-2 ring-foreground px-5 py-2.5 mt-1">
              <p className="text-base font-bold tabular-nums">
                Pouzeće: {completionState.codAmount.toFixed(2)} BAM
              </p>
            </div>
          )}
          {completionState.needsShipment ? (
            <div className="flex flex-col gap-2 w-full mt-1">
              <Button
                className="h-12 gap-2 text-sm font-medium"
                onClick={(e) => {
                  // Stanica radi sa skenerom (tastatura + Enter). Bez fokusa na dugmetu nakon
                  // odustajanja od dialoga zalutali Enter ne otvara nalog ponovo (ni „Sljedeća
                  // narudžba“ ne preskače ekran) — i jedno i drugo ostaje svjesni klik.
                  // Dugme se blura prije otvaranja, pa ga zatvaranje dialoga nema čemu vratiti.
                  e.currentTarget.blur();
                  setShipmentOpen(true);
                }}
              >
                <Truck className="h-4 w-4" />
                Napravi nalog u brzoj pošti
              </Button>
              <Button variant="outline" onClick={resetState}>Sljedeća narudžba</Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Priprema za sljedeću narudžbu...</p>
          )}
        </div>
        {completionState.needsShipment && (
          <CourierShipmentDialog
            open={shipmentOpen}
            // Zatvaranje nakon kreiranog naloga (i naljepnice) vodi na sljedeću narudžbu;
            // odustajanje od forme ostavlja ovaj ekran da se nalog može napraviti kasnije.
            onOpenChange={(o) => {
              if (o) setShipmentOpen(true);
              else if (shipmentCreatedRef.current) resetState();
              else setShipmentOpen(false);
            }}
            localId={completionState.orderId}
            orderNumber={completionState.orderNumber}
            onCreated={() => { shipmentCreatedRef.current = true; }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-t-2 border-t-green-600 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-green-600/15 text-green-700 dark:text-green-400 px-2 py-0.5 text-xs font-bold tracking-wider">PACK</span>
            <span className="text-xs text-muted-foreground">završava narudžbu (fulfillment)</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <span className="text-[11px] text-muted-foreground">Auto</span>
              <Switch
                checked={autoFulfillment}
                onCheckedChange={(checked) => {
                  setAutoFulfillment(checked);
                  localStorage.setItem("komoran:autoFulfillment", String(checked));
                }}
              />
            </label>
            <PickPackSettingsDialog />
          </div>
        </div>
        {!pickItems ? (
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={orderInputRef}
              value={orderInput}
              onChange={(e) => setOrderInput(e.target.value)}
              onKeyDown={isolateScannerKeys(handleOrderSubmit)}
              placeholder="Skeniraj narudžbu, VP broj ili tracking broj"
              className="h-11 pl-9 text-sm"
              autoComplete="off"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={skuInputRef}
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value)}
                onKeyDown={isolateScannerKeys(handleSkuScan)}
                placeholder="Skeniraj artikal (SKU)"
                className="h-11 pl-9 text-sm flex-1"
                autoComplete="off"
              />
            </div>
            <Button variant="ghost" size="icon" onClick={resetState} className="shrink-0 h-11 w-11">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        )}
        {isLoading && <p className="text-xs text-muted-foreground">Učitavanje...</p>}
      </div>

      {pickItems && order && (
        <div className="flex-1 overflow-auto">
          <div className="px-4 py-3 border-b bg-muted/30 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              {meta ? (
                <MetaLookupHeader meta={meta} showPacked />
              ) : (
                <span className="text-sm font-medium">#{order.number || orderId}</span>
              )}
              <span className="text-xs tabular-nums font-medium shrink-0">{totalScanned}/{totalNeeded}</span>
            </div>
            {(order.shipping?.first_name || order.shipping?.last_name) && (
              <span className="text-base font-semibold">
                {order.shipping?.first_name} {order.shipping?.last_name}
              </span>
            )}
            <div className="h-1 bg-muted w-full">
              <div
                className="h-full bg-foreground transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {order.meta_data?.find((m: any) => m.key === "_greenbox_important_note")?.value && (
              <div className="flex items-start gap-1.5 bg-red-500/10 px-2.5 py-1.5 text-xs">
                <AlertTriangle className="h-3 w-3 text-red-600 shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap font-medium text-red-800">{order.meta_data.find((m: any) => m.key === "_greenbox_important_note").value}</span>
              </div>
            )}
            {order.meta_data?.find((m: any) => m.key === "_komoran_internal_note")?.value && (
              <div className="flex items-start gap-1.5 bg-yellow-500/10 px-2.5 py-1.5 text-xs">
                <Lock className="h-3 w-3 text-yellow-600 shrink-0 mt-0.5" />
                <span className="whitespace-pre-wrap">{order.meta_data.find((m: any) => m.key === "_komoran_internal_note").value}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col">
            {pickItems.map((item: any, idx: number) => {
              const scanned = scannedItems.find((s) => s.sku === item.sku);
              const scannedCount = scanned?.scannedCount ?? 0;
              const isDone = scannedCount >= item.quantity;
              const hasAlternates = item.alternateLocations?.length > 0;
              const overrideBin = scanned?.overrideBin;

              return (
                <div
                  key={idx}
                  className={cn(
                    "flex flex-col border-b px-4 py-3 transition-colors",
                    isDone && "bg-green-600/5"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleItemTap(item.sku, item.quantity)}
                      disabled={isDone}
                      className="shrink-0 w-7 h-7 flex items-center justify-center -m-1 active:scale-90 transition-transform disabled:pointer-events-none"
                    >
                      {isDone ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : overrideBin ? (
                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <div className="h-3 w-3 ring-2 ring-foreground/20" />
                      )}
                    </button>
                    {item.image && imgSize > 0 && (
                      <img
                        src={item.image}
                        alt={item.sku}
                        className="shrink-0 object-cover ring-1 ring-foreground/10"
                        style={{ width: imgSize, height: imgSize }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-medium truncate">{item.sku}</span>
                        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                          {scannedCount > 0 ? `${scannedCount}/${item.quantity}` : `×${item.quantity}`}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{item.name}</p>
                      <MetaSourcesLine sources={item.sources} />
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 mt-1.5">
                    <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="font-medium tabular-nums break-words min-w-0" style={{ fontSize: locFontSize }}>
                      {overrideBin || (hideWarehouseName ? item.binLabel : `${item.warehouseName} → ${item.binLabel}`)}
                    </span>
                    {hasAlternates && !isDone && (
                      <button
                        onClick={() => setShowAlternates(showAlternates === item.sku ? null : item.sku)}
                        className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground px-1 shrink-0"
                      >
                        alt
                      </button>
                    )}
                  </div>

                  {showAlternates === item.sku && (
                    <div className="ml-[18px] mt-1.5 flex flex-col gap-0.5">
                      {item.alternateLocations.map((alt: any, ai: number) => (
                        <button
                          key={ai}
                          onClick={() => handleOverride(item.sku, hideWarehouseName ? alt.binLabel : `${alt.warehouseName} → ${alt.binLabel}`)}
                          className="text-xs text-left px-2 py-1.5 hover:bg-muted transition-colors"
                        >
                          {hideWarehouseName ? alt.binLabel : `${alt.warehouseName} → ${alt.binLabel}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {allDone && completionError && !metaOutcome && (
            <p className="px-4 pt-4 text-xs text-destructive">{completionError}</p>
          )}

          {allDone && metaOutcome && (
            <div className="mx-4 mt-4 flex flex-col gap-1.5 bg-destructive/10 px-3 py-2.5 text-xs">
              <p className="flex items-center gap-1.5 font-medium text-destructive">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Pack nije završen za sve narudžbe spojene pošiljke
              </p>
              <ul className="flex flex-col gap-0.5 tabular-nums">
                {metaOutcome.map((m) => (
                  <li key={m.key} className={m.ok ? "text-green-700 dark:text-green-400" : "text-destructive"}>
                    {m.label} — {m.ok ? "završena" : `nije završena${m.error ? `: ${m.error}` : ""}`}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground">Ponovni pack dovršava samo preostale narudžbe, bez dupliranja.</p>
            </div>
          )}

          {allDone && (!autoFulfillment || completionError) && (
            <div className="p-4">
              <Button
                onClick={doFulfill}
                disabled={fulfillOrder.isPending}
                className="w-full h-12 text-sm font-medium"
              >
                {fulfillOrder.isPending
                  ? "Završavanje..."
                  : metaOutcome
                    ? "Ponovi pack za preostale"
                    : completionError
                      ? "Pokušaj ponovo"
                      : "Završi narudžbu"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
